import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { getShowScoringDate } from '../utils/factionLineupScoring';
import { getFactionStoredWrestlers } from '../utils/factionWrestlers';
import {
  buildGmMatchWrestlerToFactionMap,
  getGmMatchParticipationScore,
} from '../utils/gmMatchScoring';
import {
  formatGmMatchDate,
  getFactionGmMatches,
  getGmMatchLabel,
  getGmMatchLifecycle,
  normalizeGmMatch,
} from '../utils/gmMatches';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const MATCH_STATE_META = {
  active: {
    label: 'In corso',
    badgeClass: '',
    badgeStyle: {
      background: 'rgba(25, 135, 84, 0.22)',
      color: '#baf5cb',
      border: '1px solid rgba(25, 135, 84, 0.48)',
    },
    counterStyle: {
      background: 'rgba(25, 135, 84, 0.14)',
      color: '#d8ffe4',
      border: '1px solid rgba(25, 135, 84, 0.34)',
      boxShadow: '0 10px 24px rgba(25, 135, 84, 0.12)',
    },
    cardStyle: {
      border: '1px solid rgba(25, 135, 84, 0.26)',
      boxShadow: '0 20px 40px rgba(4, 24, 12, 0.28)',
    },
  },
  upcoming: {
    label: 'Non iniziato',
    badgeClass: '',
    badgeStyle: {
      background: 'rgba(13, 110, 253, 0.18)',
      color: '#d7e8ff',
      border: '1px solid rgba(13, 110, 253, 0.4)',
    },
    counterStyle: {
      background: 'rgba(13, 110, 253, 0.12)',
      color: '#e3eeff',
      border: '1px solid rgba(13, 110, 253, 0.28)',
      boxShadow: '0 10px 24px rgba(13, 110, 253, 0.1)',
    },
    cardStyle: {
      border: '1px solid rgba(13, 110, 253, 0.22)',
      boxShadow: '0 20px 40px rgba(4, 16, 32, 0.26)',
    },
  },
  closed: {
    label: 'Chiuso',
    badgeClass: '',
    badgeStyle: {
      background: 'rgba(108, 117, 125, 0.28)',
      color: '#f3f5f7',
      border: '1px solid rgba(173, 181, 189, 0.32)',
    },
    counterStyle: {
      background: 'rgba(108, 117, 125, 0.16)',
      color: '#f3f5f7',
      border: '1px solid rgba(173, 181, 189, 0.24)',
      boxShadow: '0 10px 24px rgba(15, 17, 20, 0.18)',
    },
    cardStyle: {
      border: '1px solid rgba(173, 181, 189, 0.2)',
      boxShadow: '0 20px 40px rgba(8, 12, 17, 0.24)',
    },
  },
  expired: {
    label: 'Chiusura in corso',
    badgeClass: '',
    badgeStyle: {
      background: 'rgba(255, 193, 7, 0.22)',
      color: '#fff1be',
      border: '1px solid rgba(255, 193, 7, 0.42)',
    },
    counterStyle: {
      background: 'rgba(255, 193, 7, 0.14)',
      color: '#fff5d6',
      border: '1px solid rgba(255, 193, 7, 0.28)',
      boxShadow: '0 10px 24px rgba(255, 193, 7, 0.1)',
    },
    cardStyle: {
      border: '1px solid rgba(255, 193, 7, 0.24)',
      boxShadow: '0 20px 40px rgba(36, 26, 4, 0.24)',
    },
  },
};

const getMatchStateMeta = (match) =>
  MATCH_STATE_META[getGmMatchLifecycle(match)] || MATCH_STATE_META.closed;

const LOCAL_PAGE_STYLES = `
  .faction-matches-page .faction-match-stat-pill {
    flex: 1 1 170px;
    min-width: 0;
    text-align: center;
  }

  .faction-matches-page .faction-match-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .faction-matches-page .faction-match-badge {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .faction-matches-page .faction-match-period,
  .faction-matches-page .faction-match-closing {
    min-width: 0;
  }

  .faction-matches-page .faction-match-nav {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.75rem;
  }

  .faction-matches-page .faction-match-nav-btn {
    width: 100%;
    min-width: 0;
  }

  .faction-matches-page .faction-match-count {
    min-width: 0;
    white-space: nowrap;
  }

  @media (max-width: 575.98px) {
    .faction-matches-page {
      padding-left: 0.7rem !important;
      padding-right: 0.7rem !important;
    }

    .faction-matches-page .faction-match-shell {
      padding: 0.95rem !important;
      border-radius: 1.2rem !important;
    }

    .faction-matches-page .profile-match-toolbar__stats {
      gap: 0.5rem;
    }

    .faction-matches-page .faction-match-stat-pill {
      flex-basis: calc(50% - 0.5rem);
      padding: 0.55rem 0.6rem !important;
      font-size: 0.79rem;
    }

    .faction-matches-page .faction-match-card-header {
      gap: 0.5rem;
    }

    .faction-matches-page .faction-match-badge {
      max-width: calc(50% - 0.25rem);
      padding: 0.5rem 0.65rem !important;
      font-size: 0.76rem !important;
    }

    .faction-matches-page .faction-match-period .text-light {
      font-size: 0.93rem !important;
    }

    .faction-matches-page .profile-match-summary {
      gap: 0.45rem;
    }

    .faction-matches-page .profile-match-summary-pill {
      width: 100%;
      justify-content: center;
      text-align: center;
      font-size: 0.78rem;
    }

    .faction-matches-page .profile-match-breakdown-grid {
      grid-template-columns: 1fr;
    }

    .faction-matches-page .faction-match-nav {
      gap: 0.45rem;
      padding: 0.75rem !important;
    }

    .faction-matches-page .faction-match-nav-btn {
      padding: 0.8rem 0.55rem !important;
      font-size: 0.84rem;
    }

    .faction-matches-page .faction-match-count {
      font-size: 0.78rem;
    }
  }

  @media (max-width: 379.98px) {
    .faction-matches-page .faction-match-stat-pill {
      flex-basis: 100%;
    }

    .faction-matches-page .faction-match-badge {
      max-width: none;
      width: calc(50% - 0.25rem);
    }

    .faction-matches-page .faction-match-nav-btn__label {
      display: none;
    }

    .faction-matches-page .faction-match-nav-btn i.bi-chevron-left {
      margin-right: 0 !important;
    }

    .faction-matches-page .faction-match-nav-btn i.bi-chevron-right {
      margin-left: 0 !important;
    }
  }
`;

const sortMatchesForDisplay = (matches = []) =>
  [...matches].sort((a, b) => {
    const startDiff = (a.weekStart?.getTime?.() || 0) - (b.weekStart?.getTime?.() || 0);
    if (startDiff !== 0) return startDiff;

    const endDiff = (a.weekEnd?.getTime?.() || 0) - (b.weekEnd?.getTime?.() || 0);
    if (endDiff !== 0) return endDiff;

    return (a.closedAt?.getTime?.() || 0) - (b.closedAt?.getTime?.() || 0);
  });

const getInitialSelectedMatchIndex = (matches = []) => {
  const firstActiveIndex = matches.findIndex(
    (match) => getGmMatchLifecycle(match) === 'active'
  );

  return firstActiveIndex >= 0 ? firstActiveIndex : 0;
};

const sortBreakdownFactions = (factions = [], currentFactionId = null) =>
  [...factions].sort((a, b) => {
    if (a.factionId === currentFactionId) return -1;
    if (b.factionId === currentFactionId) return 1;
    return a.factionName.localeCompare(b.factionName, 'it');
  });

function GmMatchFactionPanel({
  faction,
  currentFactionId,
  matchState,
  factionResult = null,
  isLeading = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const isCurrentFaction = faction.factionId === currentFactionId;
  const isWinner = matchState === 'closed' && Boolean(factionResult?.isWinner);
  const visibleMembers = expanded ? faction.members : faction.members.slice(0, 5);
  const hasHiddenMembers = faction.members.length > 5;

  return (
    <div
      className={[
        'profile-match-breakdown-faction',
        isCurrentFaction ? 'is-current' : '',
        isWinner ? 'is-winner' : '',
        matchState === 'active' && isLeading ? 'is-leading' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div className="min-w-0">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <strong className="text-light text-truncate">{faction.factionName}</strong>
            {matchState === 'active' && isLeading && (
              <span className="badge bg-brand-secondary text-dark">In testa</span>
            )}
            {isWinner && (
              <span className="badge bg-success">+{factionResult?.matchPoints || 0} pt</span>
            )}
          </div>
        </div>

        <span className="badge bg-dark">{faction.totalPoints} pt</span>
      </div>

      <div className="profile-match-breakdown-members">
        {visibleMembers.length > 0 ? (
          visibleMembers.map((member) => (
            <div key={member.id} className="profile-match-breakdown-member">
              <span className="text-truncate">{member.name}</span>
              <strong className={member.points > 0 ? 'text-brand-secondary' : 'text-muted-brand'}>
                {member.points}
              </strong>
            </div>
          ))
        ) : (
          <div className="small text-muted-brand">Nessun wrestler in fazione</div>
        )}
      </div>

      {hasHiddenMembers && (
        <button
          type="button"
          className="btn btn-link btn-sm px-0 pt-2 pb-0 text-brand-secondary text-decoration-none"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Mostra meno' : `Vedi tutti (${faction.members.length})`}
        </button>
      )}
    </div>
  );
}

function GmMatchBreakdown({ match, breakdown, loading, currentFactionId }) {
  const matchState = getGmMatchLifecycle(match);

  if (loading && !breakdown) {
    return (
      <div className="profile-match-breakdown">
        <div className="small text-muted-brand">Caricamento dettagli match...</div>
      </div>
    );
  }

  if (!breakdown) return null;

  const resultsByFactionId = Object.fromEntries(
    (match.results || []).map((result) => [result.factionId, result])
  );
  const orderedFactions = sortBreakdownFactions(breakdown.factions, currentFactionId);
  const topScore = orderedFactions.reduce(
    (max, faction) => Math.max(max, faction.totalPoints),
    Number.NEGATIVE_INFINITY
  );
  const leadingFactionIds =
    matchState === 'active' && topScore > 0
      ? new Set(
        orderedFactions
          .filter((faction) => faction.totalPoints === topScore)
          .map((faction) => faction.factionId)
      )
      : new Set();

  return (
    <div className="profile-match-breakdown">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
        <div className="small text-muted-brand">Dettaglio wrestler nel periodo del match</div>
      </div>

      <div className="profile-match-breakdown-grid">
        {orderedFactions.map((faction) => (
          <GmMatchFactionPanel
            key={faction.factionId}
            faction={faction}
            currentFactionId={currentFactionId}
            matchState={matchState}
            factionResult={resultsByFactionId[faction.factionId] || null}
            isLeading={leadingFactionIds.has(faction.factionId)}
          />
        ))}
      </div>
    </div>
  );
}

function GmMatchCard({ match, factionId, breakdown, loadingBreakdown }) {
  const matchState = getGmMatchLifecycle(match);
  const stateMeta = getMatchStateMeta(match);
  const results = Array.isArray(match.results) ? match.results : [];
  const factionResult = results.find((result) => result.factionId === factionId) || null;
  const winnerNames = results.filter((result) => result.isWinner).map((result) => result.factionName);
  const periodLabel = `${formatGmMatchDate(match.weekStart)} - ${formatGmMatchDate(match.weekEnd)}`;
  const closingLabel =
    matchState === 'closed' && match.closedAt
      ? `Chiuso il ${formatGmMatchDate(match.closedAt)}`
      : `Chiusura ${formatGmMatchDate(match.weekEnd)}`;

  return (
    <div
      className={`profile-match-card profile-match-card--${matchState} faction-match-card`}
      style={{
        ...stateMeta.cardStyle,
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.03)) rgba(8,12,17,0.96)',
        borderRadius: '1.2rem',
      }}
    >
      <div className="faction-match-card-header mb-3">
        <span
          className="badge faction-match-badge"
          style={{
            background: 'rgba(255,255,255,0.06)',
            color: '#f8f9fa',
            border: '1px solid rgba(255,255,255,0.09)',
            padding: '0.55rem 0.8rem',
            fontSize: '0.82rem',
            fontWeight: 700,
            letterSpacing: '0.01em',
          }}
        >
          {getGmMatchLabel(match.matchType)}
        </span>
        <span
          className={`badge faction-match-badge ${stateMeta.badgeClass}`}
          style={{
            ...stateMeta.badgeStyle,
            padding: '0.55rem 0.8rem',
            fontSize: '0.82rem',
            fontWeight: 700,
            letterSpacing: '0.01em',
          }}
        >
          {stateMeta.label}
        </span>
      </div>

      <div className="text-center mb-3 faction-match-period">
        <div
          className="small text-uppercase fw-semibold mb-1"
          style={{ letterSpacing: '0.08em', color: 'rgba(255,255,255,0.58)', fontSize: '0.7rem' }}
        >
          Periodo
        </div>
        <div className="text-light fw-semibold" style={{ fontSize: '1rem' }}>
          {periodLabel}
        </div>
      </div>

      <div className="profile-match-summary justify-content-center">
        {matchState === 'closed' ? (
          <>
            {factionResult && (
              <span className="profile-match-summary-pill">
                Tu: <strong>{factionResult.weeklyScore || 0}</strong> pt
              </span>
            )}
            {factionResult?.isWinner ? (
              <span className="profile-match-summary-pill profile-match-summary-pill--success">
                Hai vinto: +{factionResult.matchPoints || 0} pt
              </span>
            ) : winnerNames.length > 0 ? (
              <span className="profile-match-summary-pill">
                Vince: <strong>{winnerNames.join(', ')}</strong>
              </span>
            ) : (
              <span className="profile-match-summary-pill">Risultato non disponibile</span>
            )}
          </>
        ) : matchState === 'active' ? (
          <span className="profile-match-summary-pill">
            I punti si aggiornano solo sugli show compresi in questo intervallo.
          </span>
        ) : (
          <span className="profile-match-summary-pill">
            Questo match non e ancora iniziato.
          </span>
        )}
      </div>

      <GmMatchBreakdown
        match={match}
        breakdown={breakdown}
        loading={loadingBreakdown}
        currentFactionId={factionId}
      />

      <div
        className="text-center mt-3 pt-3 faction-match-closing"
        style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="small text-muted-brand">{closingLabel}</div>
      </div>
    </div>
  );
}

export default function FactionMatchesPage() {
  const { id: factionId } = useParams();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingBreakdowns, setLoadingBreakdowns] = useState(false);
  const [error, setError] = useState('');
  const [faction, setFaction] = useState(null);
  const [roster, setRoster] = useState(null);
  const [matches, setMatches] = useState([]);
  const [breakdowns, setBreakdowns] = useState({});
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setSelectedMatchIndex((prev) => {
      if (matches.length === 0) return 0;
      return Math.min(prev, matches.length - 1);
    });
  }, [matches.length]);

  const resolveRoster = useCallback(async (factionData) => {
    if (factionData.rosterId) {
      const rosterSnap = await getDoc(doc(db, 'rosters', factionData.rosterId));
      return rosterSnap.exists() ? { id: rosterSnap.id, ...rosterSnap.data() } : null;
    }

    if (factionData.roster) {
      const rosterSnap = await getDocs(
        query(collection(db, 'rosters'), where('name', '==', factionData.roster))
      );

      if (!rosterSnap.empty) {
        return { id: rosterSnap.docs[0].id, ...rosterSnap.docs[0].data() };
      }
    }

    return null;
  }, []);

  const loadBreakdowns = useCallback(async (nextMatches, rosterData = null) => {
    if (!nextMatches.length) {
      setBreakdowns({});
      return;
    }

    setLoadingBreakdowns(true);
    try {
      const factionIds = Array.from(new Set(
        nextMatches.flatMap((match) =>
          (match.participants || []).map((participant) => participant.factionId).filter(Boolean)
        )
      ));

      const [wrestlersSnap, factionDocs] = await Promise.all([
        getDocs(collection(db, 'wrestlers')),
        Promise.all(factionIds.map((matchFactionId) => getDoc(doc(db, 'factions', matchFactionId)))),
      ]);

      const wrestlersMap = {};
      wrestlersSnap.forEach((wrestlerDoc) => {
        const data = wrestlerDoc.data();
        wrestlersMap[wrestlerDoc.id] = {
          id: wrestlerDoc.id,
          name: data.name || data.nome || 'Wrestler',
          imageUrl: data.imageUrl || '/assets/default-wrestler.png',
        };
      });

      const factionsById = {};
      factionDocs.forEach((factionDoc) => {
        if (factionDoc.exists()) {
          factionsById[factionDoc.id] = { id: factionDoc.id, ...factionDoc.data() };
        }
      });

      const foreignRosterIds = [...new Set(
        factionIds
          .map((matchFactionId) => factionsById[matchFactionId]?.rosterId)
          .filter(Boolean)
          .filter((rosterId) => rosterId !== rosterData?.id)
      )];

      const rosterDocs = await Promise.all(
        foreignRosterIds.map((rosterId) => getDoc(doc(db, 'rosters', rosterId)))
      );
      const rosterMap = rosterDocs.reduce((acc, rosterDoc) => {
        if (rosterDoc.exists()) {
          acc[rosterDoc.id] = { id: rosterDoc.id, ...rosterDoc.data() };
        }
        return acc;
      }, {});

      const showEntriesCache = {};
      const getShowEntriesForMatch = async (match) => {
        const weekStart = match.weekStart ? startOfDay(match.weekStart) : null;
        const weekEnd = match.weekEnd ? endOfDay(match.weekEnd) : null;

        if (!weekStart || !weekEnd || Number.isNaN(weekStart.getTime()) || Number.isNaN(weekEnd.getTime())) {
          return [];
        }

        const cacheKey = `${weekStart.getTime()}-${weekEnd.getTime()}`;
        if (showEntriesCache[cacheKey]) return showEntriesCache[cacheKey];

        const showsQuery = query(
          collection(db, 'shows'),
          where('data', '>=', Timestamp.fromDate(weekStart)),
          where('data', '<=', Timestamp.fromDate(weekEnd))
        );
        const showsSnap = await getDocs(showsQuery);

        const showEntries = await Promise.all(
          showsSnap.docs.map(async (showDoc) => {
            const participationsSnap = await getDocs(collection(db, 'shows', showDoc.id, 'participations'));
            return {
              show: { id: showDoc.id, ...showDoc.data() },
              participations: participationsSnap.docs.map((participationDoc) => ({
                id: participationDoc.id,
                ...participationDoc.data(),
              })),
            };
          })
        );

        showEntriesCache[cacheKey] = showEntries;
        return showEntries;
      };

      const breakdownEntries = await Promise.all(
        nextMatches.map(async (match) => {
          const wrestlerToFactionId = buildGmMatchWrestlerToFactionMap(match, factionsById);
          const showEntries = await getShowEntriesForMatch(match);
          const memberPointsByFaction = {};
          const rostersByFactionId = {};

          (match.participants || []).forEach((participant) => {
            const matchFaction = factionsById[participant.factionId];
            const matchFactionRosterId = matchFaction?.rosterId;

            rostersByFactionId[participant.factionId] =
              (matchFactionRosterId && rosterMap[matchFactionRosterId]) || rosterData || null;

            memberPointsByFaction[participant.factionId] = {};
            getFactionStoredWrestlers(matchFaction).forEach((member) => {
              memberPointsByFaction[participant.factionId][member.id] = 0;
            });
          });

          showEntries.forEach((entry) => {
            const showDate = getShowScoringDate(entry.show);

            (entry.participations || []).forEach((participation) => {
              const ownerFactionId = wrestlerToFactionId[participation.wrestlerId];
              if (!ownerFactionId) return;

              const scoredPoints = getGmMatchParticipationScore({
                faction: factionsById[ownerFactionId],
                roster: rostersByFactionId[ownerFactionId] || null,
                participation,
                showDate,
              });

              memberPointsByFaction[ownerFactionId][participation.wrestlerId] =
                (memberPointsByFaction[ownerFactionId][participation.wrestlerId] || 0) + scoredPoints;
            });
          });

          const factions = (match.participants || []).map((participant) => {
            const matchFaction = factionsById[participant.factionId];
            const members = getFactionStoredWrestlers(matchFaction)
              .map((member) => {
                const wrestler = wrestlersMap[member.id] || {};
                return {
                  id: member.id,
                  name: wrestler.name || member.name || 'Wrestler',
                  imageUrl: wrestler.imageUrl || member.imageUrl || '/assets/default-wrestler.png',
                  points: memberPointsByFaction[participant.factionId]?.[member.id] || 0,
                };
              })
              .sort((a, b) => b.points - a.points || a.name.localeCompare(b.name, 'it'));

            return {
              factionId: participant.factionId,
              factionName: matchFaction?.name || participant.factionName || 'Fazione',
              totalPoints: members.reduce((sum, member) => sum + member.points, 0),
              members,
            };
          });

          return [match.id, { factions }];
        })
      );

      setBreakdowns(Object.fromEntries(breakdownEntries));
    } catch (loadError) {
      console.error(loadError);
      setBreakdowns({});
    } finally {
      setLoadingBreakdowns(false);
    }
  }, []);

  const loadPageData = useCallback(async () => {
    if (!factionId || !currentUser) return;

    setLoading(true);
    setError('');

    try {
      const factionSnap = await getDoc(doc(db, 'factions', factionId));
      if (!factionSnap.exists()) {
        setError('Fazione non trovata');
        return;
      }

      const factionData = { id: factionSnap.id, ...factionSnap.data() };
      if (factionData.ownerUid !== currentUser.uid) {
        setError('Non sei autorizzato a vedere i match di questa fazione');
        return;
      }

      const rosterData = await resolveRoster(factionData);
      setFaction(factionData);
      setRoster(rosterData);

      if (!rosterData?.id) {
        setMatches([]);
        setBreakdowns({});
        return;
      }

      const matchesSnap = await getDocs(collection(db, 'rosters', rosterData.id, 'matches'));
      const normalizedMatches = matchesSnap.docs.map((matchDoc) => normalizeGmMatch(matchDoc));
      const { matches: syncedMatches } = await autoCloseExpiredGmMatches({
        db,
        rosterId: rosterData.id,
        matches: normalizedMatches,
        roster: rosterData,
        persist: true,
      });
      const factionMatches = sortMatchesForDisplay(getFactionGmMatches(syncedMatches, factionData.id));

      setMatches(factionMatches);
      setSelectedMatchIndex(getInitialSelectedMatchIndex(factionMatches));
      await loadBreakdowns(factionMatches, rosterData);
    } catch (loadError) {
      console.error(loadError);
      setError('Errore durante il caricamento dei match');
    } finally {
      setLoading(false);
    }
  }, [currentUser, factionId, loadBreakdowns, resolveRoster]);

  useEffect(() => {
    if (!authLoading && currentUser) {
      loadPageData();
    }

    if (!authLoading && !currentUser) {
      setLoading(false);
    }
  }, [authLoading, currentUser, loadPageData]);

  const matchCounters = useMemo(
    () =>
      matches.reduce((acc, match) => {
        const lifecycle = getGmMatchLifecycle(match);
        if (lifecycle === 'active') acc.active += 1;
        else if (lifecycle === 'upcoming') acc.upcoming += 1;
        else acc.closed += 1;
        return acc;
      }, { active: 0, upcoming: 0, closed: 0 }),
    [matches]
  );

  const selectedMatch = matches[selectedMatchIndex] || null;

  if (authLoading || loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!currentUser) {
    return (
      <div className="profile-page-shell px-3 px-md-4 py-5">
        <div className="profile-panel-card text-center">
          <h4 className="brand-title mb-3">Accesso richiesto</h4>
          <Link to="/auth" className="btn btn-brand-secondary">Accedi</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page-shell px-3 px-md-4 py-5">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-outline-brand-secondary" onClick={() => navigate('/profile')}>
          Torna al profilo
        </button>
      </div>
    );
  }

  return (
    <div className="profile-page-shell px-3 px-md-4 py-4 faction-matches-page">
      <style>{LOCAL_PAGE_STYLES}</style>
      <div
        className="profile-panel-card profile-match-section w-100 faction-match-shell"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015)) rgba(6,10,14,0.96)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '1.5rem',
          padding: '1.15rem',
        }}
      >
        <div className="profile-panel-head profile-match-section__head text-center">
          <div className="w-100">
            <h3 className="profile-panel-eyebrow brand-title mb-0 text-center text-uppercase">I tuoi match</h3>
            <p className="brand-title mb-0 text-center text-uppercase">{roster?.name || 'Roster GM'}</p>
          </div>
        </div>

        <div className="profile-match-toolbar justify-content-center">
          <div className="profile-match-toolbar__stats justify-content-center w-100">
            <span
              className="profile-match-toolbar__pill flex-fill flex-sm-grow-0 justify-content-center faction-match-stat-pill"
              style={MATCH_STATE_META.active.counterStyle}
            >
              <strong>{matchCounters.active}</strong> in corso
            </span>
            <span
              className="profile-match-toolbar__pill flex-fill flex-sm-grow-0 justify-content-center faction-match-stat-pill"
              style={MATCH_STATE_META.upcoming.counterStyle}
            >
              <strong>{matchCounters.upcoming}</strong> da iniziare
            </span>
            <span
              className="profile-match-toolbar__pill flex-fill flex-sm-grow-0 justify-content-center faction-match-stat-pill"
              style={MATCH_STATE_META.closed.counterStyle}
            >
              <strong>{matchCounters.closed}</strong> chiusi
            </span>
          </div>
        </div>

        {matches.length === 0 ? (
          <div className="profile-inline-empty">
            Nessun match GM associato a questa fazione.
          </div>
        ) : (
          <div className="profile-match-single-view">
            {selectedMatch && (
              <GmMatchCard
                match={selectedMatch}
                factionId={faction.id}
                breakdown={breakdowns[selectedMatch.id]}
                loadingBreakdown={loadingBreakdowns}
              />
            )}

            <div
              className="profile-match-single-view__nav faction-match-nav mt-3"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '1.2rem',
                padding: '0.85rem',
              }}
            >
              <button
                type="button"
                className="btn btn-outline-light rounded-pill px-4 py-3 fw-semibold faction-match-nav-btn"
                onClick={() => setSelectedMatchIndex((prev) => Math.max(prev - 1, 0))}
                disabled={selectedMatchIndex === 0}
              >
                <i className="bi bi-chevron-left me-2"></i>
                <span className="faction-match-nav-btn__label">Prec.</span>
              </button>

              <div
                className="small fw-semibold text-center faction-match-count"
                style={{ color: 'rgba(255,255,255,0.78)' }}
              >
                {selectedMatchIndex + 1} di {matches.length}
              </div>

              <button
                type="button"
                className="btn btn-outline-light rounded-pill px-4 py-3 fw-semibold faction-match-nav-btn"
                onClick={() => setSelectedMatchIndex((prev) => Math.min(prev + 1, matches.length - 1))}
                disabled={selectedMatchIndex === matches.length - 1}
              >
                <span className="faction-match-nav-btn__label">Succ.</span>
                <i className="bi bi-chevron-right ms-2"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
